import { AnimatedPage, AnimatedPageHeader } from '@components/index'
import { Loader } from '@components/loader/Loader'
import ContractDashboard from '@features/contracts/components/ContractDashboard'
import Documents from '@features/contracts/components/Documents'
import Rents from '@features/contracts/components/Rents'
import { useContracts } from '@services/contracts/queires/useContracts'
import { useRents } from '@services/contracts/queires/useRents'
import { UserSettlementsList } from '@features/settlements/components/UserSettlementsList'
import {
  CreditCard,
  FileText,
  LayoutDashboard,
  Receipt,
  ScrollText
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

const TAB_KEYS = ['dashboard', 'payments', 'settlements', 'documents']

import { cn } from '@/lib/utils'

export function Contract() {
  const { data: contracts, isLoading: contractsIsLoading } = useContracts()
  const { data: rents, isLoading: rentsIsLoading } = useRents()
  const { t } = useTranslation('contract')
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const active = tabParam && TAB_KEYS.includes(tabParam) ? tabParam : 'dashboard'
  const setActive = (key: string) =>
    setSearchParams({ tab: key }, { replace: true })

  const contract = contracts?.results[0]
  const offer = contracts?.results[0]?.offer

  const unpaidRents = rents?.results.filter((rent) => !rent.paid) ?? []
  const paidRents = rents?.results.filter((rent) => rent.paid) ?? []

  if (contractsIsLoading || rentsIsLoading) {
    return <Loader />
  }

  const tabs = [
    {
      key: 'dashboard',
      label: t('navigation.dashboard'),
      icon: LayoutDashboard,
      content: (
        <ContractDashboard
          contract={contract}
          rents={unpaidRents}
          offer={offer}
        />
      )
    },
    {
      key: 'payments',
      label: t('navigation.payments'),
      icon: CreditCard,
      content: <Rents rents={unpaidRents} paidRents={paidRents} />
    },
    {
      key: 'settlements',
      label: t('navigation.settlements'),
      icon: Receipt,
      content: <UserSettlementsList />
    },
    {
      key: 'documents',
      label: t('navigation.documents'),
      icon: FileText,
      content: <Documents contract={contract} />
    }
  ]

  return (
    <AnimatedPage>
      <div className="mx-auto w-full max-w-[1100px]">
        <AnimatedPageHeader title={t('title')} />

        {!contract ? (
          <div className="mt-2 flex flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-ink/15 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <ScrollText className="h-7 w-7" />
            </span>
            <div>
              <h3 className="m-0 font-display text-lg font-bold text-ink">
                {t('empty.title')}
              </h3>
              <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-muted-foreground">
                {t('empty.description')}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="mb-6 flex w-full gap-1 rounded-full border border-ink/[0.06] bg-ink/[0.03] p-1 sm:inline-flex sm:w-auto">
              {tabs.map((tab) => {
                const isActive = active === tab.key
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActive(tab.key)}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full border-0 px-3 py-2 text-sm font-semibold transition-colors sm:flex-initial sm:px-4',
                      isActive
                        ? 'bg-white text-ink shadow-sm'
                        : 'bg-transparent text-muted-foreground hover:text-ink'
                    )}
                  >
                    <tab.icon className="hidden h-4 w-4 sm:block" />
                    {tab.label}
                  </button>
                )
              })}
            </div>

            <div>{tabs.find((tab) => tab.key === active)?.content}</div>
          </>
        )}
      </div>
    </AnimatedPage>
  )
}
